import { AppEventPublisher, AgenticChatMode } from "src/AppEventPublisher";
import { DEFAULT_AGENTIC_CHAT_MODE } from "~shared/constants";
import {
  consumeAutoSwitchedFromChatToWrite,
  markAutoSwitchedFromChatToWrite,
  peekAutoSwitchedFromChatToWrite,
} from "./modeSwitchBackToast";

type UserDataLike = {
  data?: { agenticChatMode?: { value?: string } };
  update: (patch: { agenticChatMode: { value: string } }) => Promise<unknown> | unknown;
} | null | undefined;

/** Tracks Chat only → Allow edits promotion caused by the current Story Bible CTA only. */
let storyBibleCtaAutoPromotedFromChat = false;

export function clearStoryBibleCtaAutoPromotedFromChat(): void {
  storyBibleCtaAutoPromotedFromChat = false;
}

export function peekStoryBibleCtaAutoPromotedFromChat(): boolean {
  return storyBibleCtaAutoPromotedFromChat;
}

export function getComposerAgenticChatMode(userData: UserDataLike): string {
  return userData?.data?.agenticChatMode?.value ?? DEFAULT_AGENTIC_CHAT_MODE.value;
}

/**
 * Promote Chat only → Allow edits when a Story Bible CTA needs to write.
 * Marks the auto-switch so PersistentChatUI can offer switching back after the run.
 */
export function promoteComposerToAllowEditsForStoryBibleCta(options?: {
  userData?: UserDataLike;
  onModeChange?: (mode: "write") => void;
}): boolean {
  const userData = options?.userData;
  const current = getComposerAgenticChatMode(userData);
  if (current !== "chat") {
    storyBibleCtaAutoPromotedFromChat = false;
    return false;
  }

  storyBibleCtaAutoPromotedFromChat = true;
  markAutoSwitchedFromChatToWrite();
  AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Chat, AgenticChatMode.Write);

  if (options?.onModeChange) {
    options.onModeChange("write");
  } else {
    void userData?.update({ agenticChatMode: { value: "write" } });
  }

  return true;
}

/** Undo a pre-run Chat only → Allow edits promotion when the user cancels before Go. */
export function restoreComposerToChatOnlyAfterStoryBibleCtaCancel(options?: {
  userData?: UserDataLike;
  onModeChange?: (mode: "chat") => void;
}): boolean {
  if (!peekStoryBibleCtaAutoPromotedFromChat()) return false;

  storyBibleCtaAutoPromotedFromChat = false;
  if (peekAutoSwitchedFromChatToWrite()) {
    consumeAutoSwitchedFromChatToWrite();
  }
  AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Write, AgenticChatMode.Chat);

  if (options?.onModeChange) {
    options.onModeChange("chat");
  } else {
    void options?.userData?.update({ agenticChatMode: { value: "chat" } });
  }

  return true;
}

export function restoreComposerToChatOnlyIfStoryBibleCtaCancelledBeforeRun(
  ctaStatusBeforeCancel: string | undefined,
  options?: {
    userData?: UserDataLike;
    onModeChange?: (mode: "chat") => void;
  },
): boolean {
  if (ctaStatusBeforeCancel !== "awaiting_input") return false;
  return restoreComposerToChatOnlyAfterStoryBibleCtaCancel(options);
}
